import { Component, EventEmitter, Input, OnInit, Output, WritableSignal, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, EMPTY, Observable, catchError, first, map, tap } from "rxjs";
import { ApiException, ComponentPropertyViewModel, EsraPipingAPIClient, ShareLinkResponse, UsedIn, ValveViewModel } from "src/app/shared/models/autogenerated-piping";
import { getApiErrorMessage } from "src/app/shared/utils";
import { indicate } from "src/app/operators";
import { faDownload, faChevronLeft, faArrowUp, faArrowDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { DownloadService } from "src/app/core/services/download.service";
import { Router } from '@angular/router';

/** Columns the "used in" table can be sorted by; property names match `UsedIn`. */
type UsedInSortColumn = 'pipeClassName' | 'application' | 'businessUnit';

/** Default multi-level sort order applied after the user's chosen column, all ascending. */
const DEFAULT_USED_IN_SORT_ORDER: readonly UsedInSortColumn[] = ['businessUnit', 'application', 'pipeClassName'];

@Component({
    selector: 'app-valve-summary-page',
    templateUrl: './valve-summary-page.component.html',
    styleUrls: ['./valve-summary-page.component.scss'],
    standalone: false
})
export class ValveSummaryPageComponent implements OnInit {
    @Input() valveId: string;
    @Output() closeValvePage = new EventEmitter<boolean>();

    openState = true;
    faDownload = faDownload;
    faChevronLeft = faChevronLeft;
    faArrowUp = faArrowUp;
    faArrowDown = faArrowDown;
    faSort = faSort;
    isEditMode = false;
    pipeClasses: { name: string, id: string }[] = [];
    componentPropertiesToDisplay: ComponentPropertyViewModel[] = [];
    valveViewModel: ValveViewModel;
    valveViewModel$: Observable<ValveViewModel>;
    loadingIndicator$ = new BehaviorSubject<boolean>(false);

    /** Why the valve could not be shown (no access, not found, ...). */
    readonly loadErrorMessage: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

    /** Column currently driving the "used in" table sort order. Defaults to business unit. */
    activeSortColumn: UsedInSortColumn = 'businessUnit';
    /** Direction for `activeSortColumn`; true = ascending. */
    sortAscending: boolean = true;
    /** `usedIns` from the loaded valve, sorted per `activeSortColumn`/`sortAscending`. */
    sortedUsedIns: UsedIn[] = [];

    private usedIns: UsedIn[] = [];

    constructor(
        private route: ActivatedRoute,
        private esraPipingAPIClient: EsraPipingAPIClient,
        private downloadService: DownloadService,
        private router: Router
    ) { }

    ngOnInit() {
        let initValveId = null;

        if (this.valveId) {
            initValveId = this.valveId
        } else {
            initValveId = this.route.snapshot.paramMap?.get('id');
        this.isEditMode = this.route.snapshot.queryParamMap?.get('isEditMode') === 'true';
        }

        this.valveViewModel$ = this.esraPipingAPIClient.getValveViewModelById(initValveId)
            .pipe(
                indicate(this.loadingIndicator$),
                first(),
                catchError((error: unknown) => {
                    const message: string = ApiException.isApiException(error) && error.status === 404
                        ? 'The valve could not be found. It may have been removed, or the link may be incorrect.'
                        : getApiErrorMessage(error, 'The valve could not be loaded. Please try again later.');
                    this.loadErrorMessage.set(message);
                    return EMPTY;
                }),
                tap((valveVM) => {
                    this.prepareValveData(valveVM);
                })
            );
    }

    private prepareValveData(valveVM: ValveViewModel) {
        valveVM.pipeClassNames.forEach((name, index) => {
                this.pipeClasses.push({ name: name, id: valveVM.pipeClassIds[index] })
            });
        // Only display properties that have a value — properties saved with an empty
        // value (e.g. from a template where the user left a field blank) are hidden.
        const _freeFieldNames = new Set(['freeFieldPropertyName', 'freeFieldPropertyValue']);
        this.componentPropertiesToDisplay = valveVM.componentProperties
          .filter(p => p.value && p.value.trim() !== '' && !_freeFieldNames.has(p.name))
          .sort((a, b) => a.displayOrder - b.displayOrder);

        this.usedIns = valveVM.usedIns ?? [];
        this.applyUsedInsSort();

        return valveVM;
    }

    replaceUnderscoreWithSpace(value: string) {
        return value.replace("_", " ");
    }

    exportToPdf(guid: string) {
        return this.downloadService.downloadFile(
          this.esraPipingAPIClient
            .exportValveToPdf(guid)
            .pipe(indicate(this.loadingIndicator$))
        );
      }

    exportToExcel(guid: string) {
        return this.downloadService.downloadFile(
            this.esraPipingAPIClient
            .exportValveToExcel(guid)
            .pipe(indicate(this.loadingIndicator$))
        );
    }

    /**
     * Builds the fetch function passed to `app-share-link-button` for a given valve.
     * The backend returns a relative URL without a leading slash; the frontend origin is
     * prepended here since the shared button component only deals in absolute, ready-to-copy URLs.
     * @param valveId The valve id to build the share link for.
     * @returns A function returning an Observable of the absolute share URL.
     */
    getValveShareUrlFn = (valveId: string): () => Observable<string> =>
        () => this.esraPipingAPIClient.valveShareLink(valveId).pipe(
            map((response: ShareLinkResponse) => `${window.location.origin}/${(response.relativeUrl ?? '').replace(/^\/+/, '')}`)
        );

    /**
     * Handles a "used in" column header click. Toggles direction when re-clicking the
     * active column; starts ascending when switching to a new column.
     * @param column The `UsedIn` property the table should sort by.
     */
    sortTable(column: UsedInSortColumn): void {
        if (this.activeSortColumn === column) {
            this.sortAscending = !this.sortAscending;
        } else {
            this.activeSortColumn = column;
            this.sortAscending = true;
        }
        this.applyUsedInsSort();
    }

    /**
     * Rebuilds `sortedUsedIns` from `usedIns` using the sort priority table:
     * the user-selected column/direction first, then business unit, application,
     * and pipe class ascending (skipping whichever of those is already the active column).
     */
    private applyUsedInsSort(): void {
        const sortPriority: { column: UsedInSortColumn; ascending: boolean }[] = [
            { column: this.activeSortColumn, ascending: this.sortAscending },
            ...DEFAULT_USED_IN_SORT_ORDER
                .filter(column => column !== this.activeSortColumn)
                .map(column => ({ column, ascending: true }))
        ];

        this.sortedUsedIns = [...this.usedIns].sort((a, b) => {
            for (const { column, ascending } of sortPriority) {
                const comparison: number = this.getUsedInValue(a, column).localeCompare(this.getUsedInValue(b, column));
                if (comparison !== 0) {
                    return ascending ? comparison : -comparison;
                }
            }
            return 0;
        });
    }

    private getUsedInValue(usedIn: UsedIn, column: UsedInSortColumn): string {
        return usedIn[column] ?? '';
    }

    closeValveSummaryPage(): void {
        this.router.navigate(['/piping/adminSearch']);
    }

    navigateToEdit(valveId: string): void {
        this.router.navigate(['/piping/valve/edit'], {
            queryParams: { mode: 'edit', id: valveId }
        });
    }

    areThreAnyNotes(): boolean {
        return this.componentPropertiesToDisplay.filter(property => property.notes && property.notes.length > 0).length > 0;
    }
}
